import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { operationalAlertsService } from '../services/api';
import './OperationalAlerts.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

const SEVERITY_LABELS = {
    CRITICAL: 'Critico',
    HIGH: 'Alta',
    MEDIUM: 'Media',
    LOW: 'Bassa',
    INFO: 'Info',
};

const STATUS_LABELS = {
    NEW: 'Nuovo',
    ACKNOWLEDGED: 'In carico',
    SNOOZED: 'Sospeso',
    RESOLVED: 'Risolto',
    AUTO_RESOLVED: 'Auto-risolto',
};

const CATEGORY_LABELS = {
    DEVICE: 'Device',
    ONBOARDING: 'Attivazione',
    WORKER: 'Worker',
    PAYMENT: 'Pagamenti',
    PAYOUT: 'Payout',
    INVOICING: 'Fatturazione',
    DATA_QUALITY: 'Qualità dati',
};

const TABS = [
    { key: 'all', label: 'Tutti', categories: null },
    { key: 'device', label: 'Device', categories: ['DEVICE'] },
    { key: 'onboarding', label: 'Attivazione', categories: ['ONBOARDING'] },
    { key: 'worker', label: 'Worker / Stripe', categories: ['WORKER', 'PAYOUT'] },
    { key: 'payment', label: 'Pagamenti', categories: ['PAYMENT', 'DATA_QUALITY'] },
    { key: 'invoicing', label: 'Fatturazione', categories: ['INVOICING'] },
    { key: 'resolved', label: 'Risolti', statuses: ['RESOLVED', 'AUTO_RESOLVED'] },
];

const SNOOZE_OPTIONS = [
    { label: '1 ora', hours: 1 },
    { label: '4 ore', hours: 4 },
    { label: '24 ore', hours: 24 },
    { label: '48 ore', hours: 48 },
    { label: '7 giorni', hours: 168 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
    if (!iso) return '–';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}:${min}`;
}

function SeverityBadge({ severity }) {
    return (
        <span className={`oa-severity sev-${severity}`}>
            <span className="oa-severity-dot" />
            {SEVERITY_LABELS[severity] || severity}
        </span>
    );
}

function StatusBadge({ status }) {
    return (
        <span className={`oa-status-badge status-${status}`}>
            {STATUS_LABELS[status] || status}
        </span>
    );
}

function CategoryBadge({ category }) {
    return (
        <span className="oa-category-badge">
            {CATEGORY_LABELS[category] || category}
        </span>
    );
}

// ── Snooze Modal ──────────────────────────────────────────────────────────────

function SnoozeModal({ onConfirm, onCancel }) {
    const [selected, setSelected] = useState(24);
    return (
        <div className="oa-snooze-modal" onClick={onCancel}>
            <div className="oa-snooze-modal-box" onClick={e => e.stopPropagation()}>
                <h3>⏸ Sospendi alert</h3>
                <div className="oa-snooze-options">
                    {SNOOZE_OPTIONS.map(o => (
                        <button
                            key={o.hours}
                            className={`oa-snooze-option${selected === o.hours ? ' selected' : ''}`}
                            onClick={() => setSelected(o.hours)}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
                <div className="oa-snooze-modal-actions">
                    <button className="oa-btn oa-btn--secondary" onClick={onCancel}>Annulla</button>
                    <button className="oa-btn oa-btn--primary" onClick={() => onConfirm(selected)}>
                        Sospendi {SNOOZE_OPTIONS.find(o => o.hours === selected)?.label}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────

function AlertDrawer({ alert, onClose, onAction }) {
    const navigate = useNavigate();
    const [showSnooze, setShowSnooze] = useState(false);
    const [loading, setLoading] = useState(false);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const isOpen = !['RESOLVED', 'AUTO_RESOLVED'].includes(alert.status);

    async function doAction(fn) {
        setLoading(true);
        try { await fn(); onAction(); }
        catch (err) { alert(`Errore: ${err.message}`); }
        finally { setLoading(false); }
    }

    const evidence = alert.evidenceJson
        ? JSON.stringify(alert.evidenceJson, null, 2)
        : null;

    return (
        <>
            <div className="oa-drawer-overlay" onClick={onClose} />
            <div className="oa-drawer" role="dialog" aria-modal="true">
                {/* Header */}
                <div className="oa-drawer-header">
                    <div>
                        <p className="oa-drawer-title">{alert.title}</p>
                        <div className="oa-drawer-meta">
                            <SeverityBadge severity={alert.severity} />
                            <StatusBadge status={alert.status} />
                            <CategoryBadge category={alert.category} />
                        </div>
                    </div>
                    <button className="oa-drawer-close" onClick={onClose} aria-label="Chiudi">✕</button>
                </div>

                {/* Body */}
                <div className="oa-drawer-body">

                    {/* Description */}
                    <div className="oa-drawer-section">
                        <h4>Descrizione</h4>
                        <p>{alert.description}</p>
                    </div>

                    {/* Suggested action */}
                    {alert.suggestedAction && (
                        <div className="oa-drawer-section">
                            <h4>Azione suggerita</h4>
                            <div className="oa-suggested-action">{alert.suggestedAction}</div>
                        </div>
                    )}

                    {/* Entity links */}
                    <div className="oa-drawer-section">
                        <h4>Entità collegate</h4>
                        <div className="oa-entity-links">
                            {alert.tenantId && (
                                <Link className="oa-entity-link" to={`/admin/tenant/${alert.tenantId}`} onClick={onClose}>
                                    🏢 Tenant: {alert.tenant?.company_name || alert.tenantId}
                                </Link>
                            )}
                            {alert.venueId && (
                                <Link className="oa-entity-link" to={`/admin/venue/${alert.venueId}`} onClick={onClose}>
                                    🏪 Venue: {alert.venue?.name || alert.venueId}
                                </Link>
                            )}
                            {alert.deviceId && (
                                <button className="oa-entity-link" style={{ cursor: 'pointer', border: '1px solid #d1d5db' }}
                                    onClick={() => { navigate('/admin/devices'); onClose(); }}>
                                    📱 Device: {alert.deviceId.slice(0, 8)}…
                                </button>
                            )}
                            {alert.workerId && (
                                <button className="oa-entity-link" style={{ cursor: 'pointer', border: '1px solid #d1d5db' }}
                                    onClick={() => { navigate('/admin/workers'); onClose(); }}>
                                    👤 Worker: {alert.workerId.slice(0, 8)}…
                                </button>
                            )}
                            {alert.paymentIntentId && (
                                <Link className="oa-entity-link" to={`/admin/tip-transactions`} onClick={onClose}>
                                    💳 Transazione: {alert.paymentIntentId.slice(0, 8)}…
                                </Link>
                            )}
                            {alert.invoiceId && (
                                <Link className="oa-entity-link" to={`/admin/invoices/monitoring`} onClick={onClose}>
                                    🧾 Fattura/Run: {alert.invoiceId.slice(0, 8)}…
                                </Link>
                            )}
                            {!alert.tenantId && !alert.venueId && !alert.deviceId && !alert.workerId && !alert.paymentIntentId && !alert.invoiceId && (
                                <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Nessuna entità collegata</span>
                            )}
                        </div>
                    </div>

                    {/* Evidence */}
                    {evidence && (
                        <div className="oa-drawer-section">
                            <h4>Evidenze</h4>
                            <pre className="oa-evidence-box">{evidence}</pre>
                        </div>
                    )}

                    {/* Timeline */}
                    <div className="oa-drawer-section">
                        <h4>Timeline</h4>
                        <div className="oa-timeline">
                            <div className="oa-timeline-row"><span className="oa-timeline-label">Prima occorrenza:</span><span>{formatDate(alert.firstSeenAt)}</span></div>
                            <div className="oa-timeline-row"><span className="oa-timeline-label">Ultima occorrenza:</span><span>{formatDate(alert.lastSeenAt)}</span></div>
                            <div className="oa-timeline-row"><span className="oa-timeline-label">Occorrenze:</span><span>{alert.occurrenceCount}</span></div>
                            {alert.acknowledgedAt && (
                                <div className="oa-timeline-row"><span className="oa-timeline-label">Preso in carico:</span><span>{formatDate(alert.acknowledgedAt)}</span></div>
                            )}
                            {alert.snoozedUntil && (
                                <div className="oa-timeline-row"><span className="oa-timeline-label">Sospeso fino a:</span><span>{formatDate(alert.snoozedUntil)}</span></div>
                            )}
                            {alert.resolvedAt && (
                                <div className="oa-timeline-row"><span className="oa-timeline-label">Risolto il:</span><span>{formatDate(alert.resolvedAt)}</span></div>
                            )}
                            {alert.resolvedReason && (
                                <div className="oa-timeline-row"><span className="oa-timeline-label">Motivo risoluzione:</span><span>{alert.resolvedReason}</span></div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer actions */}
                {isOpen && (
                    <div className="oa-drawer-footer">
                        {alert.status !== 'ACKNOWLEDGED' && (
                            <button
                                className="oa-btn oa-btn--primary"
                                disabled={loading}
                                onClick={() => doAction(() => operationalAlertsService.acknowledge(alert.id))}
                            >
                                ✅ Prendi in carico
                            </button>
                        )}
                        <button
                            className="oa-btn oa-btn--secondary"
                            disabled={loading}
                            onClick={() => setShowSnooze(true)}
                        >
                            ⏸ Snooze
                        </button>
                        <button
                            className="oa-btn oa-btn--danger"
                            disabled={loading}
                            onClick={() => {
                                const reason = window.prompt('Motivo della risoluzione (opzionale):');
                                if (reason !== null) doAction(() => operationalAlertsService.resolve(alert.id, reason));
                            }}
                        >
                            ✓ Risolvi
                        </button>
                    </div>
                )}
            </div>

            {showSnooze && (
                <SnoozeModal
                    onCancel={() => setShowSnooze(false)}
                    onConfirm={(hours) => {
                        setShowSnooze(false);
                        doAction(() => operationalAlertsService.snooze(alert.id, hours));
                    }}
                />
            )}
        </>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OperationalAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 50, totalPages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [evaluating, setEvaluating] = useState(false);

    const [filters, setFilters] = useState({
        severity: '',
        status: '',
        tenantId: '',
        search: '',
        from: '',
        to: '',
        page: 1,
    });

    // Counts per tab (derived from summary)
    const tabCount = useCallback((tab) => {
        if (!summary) return null;
        if (tab.key === 'resolved') return (summary.byStatus?.RESOLVED || 0) + (summary.byStatus?.AUTO_RESOLVED || 0);
        if (tab.key === 'all') return summary.totalOpen;
        if (tab.categories) return tab.categories.reduce((s, c) => s + (summary.byCategory?.[c] || 0), 0);
        return null;
    }, [summary]);

    const buildQuery = useCallback(() => {
        const tab = TABS.find(t => t.key === activeTab);
        const q = { ...filters };
        if (tab) {
            if (tab.statuses) {
                q.status = tab.statuses.join(',');
            } else if (tab.categories) {
                q.category = tab.categories.join(',');
                if (!q.status) q.status = 'NEW,ACKNOWLEDGED,SNOOZED';
            } else if (tab.key === 'all') {
                if (!q.status) q.status = 'NEW,ACKNOWLEDGED,SNOOZED';
            }
        }
        return q;
    }, [filters, activeTab]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const q = buildQuery();
            const data = await operationalAlertsService.getAlerts(q);
            setAlerts(data.items || []);
            setSummary(data.summary || null);
            setPagination(data.pagination || { total: 0, page: 1, pageSize: 50, totalPages: 1 });
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    useEffect(() => { load(); }, [load]);

    function handleFilterChange(key, value) {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    }

    function handleTabChange(tabKey) {
        setActiveTab(tabKey);
        setFilters(prev => ({ ...prev, status: '', page: 1 }));
    }

    async function handleEvaluate() {
        setEvaluating(true);
        try {
            await operationalAlertsService.evaluate();
            await load();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setEvaluating(false);
        }
    }

    async function handleRowAction(fn, e) {
        e.stopPropagation();
        try { await fn(); await load(); }
        catch (err) { setError(err.message); }
    }

    function handleDrawerAction() {
        setSelectedAlert(null);
        load();
    }

    const isDev = process.env.NODE_ENV !== 'production';

    return (
        <div className="operational-alerts-page">
            {/* Page header */}
            <div className="oa-page-header">
                <div>
                    <h1>🚨 Centro Operativo</h1>
                    <p className="subtitle">Alert automatici su device, attivazioni, worker, pagamenti e fatturazione</p>
                </div>
                <div className="oa-header-actions">
                    <button className="oa-btn oa-btn--secondary" onClick={load} disabled={loading}>
                        🔄 Aggiorna
                    </button>
                    {isDev && (
                        <button className="oa-btn oa-btn--secondary" onClick={handleEvaluate} disabled={evaluating}>
                            {evaluating ? '…' : '⚙️ Valuta ora'}
                        </button>
                    )}
                </div>
            </div>

            {/* KPI cards */}
            {summary && (
                <div className="oa-kpi-grid">
                    <div className={`oa-kpi-card${activeTab === 'all' ? ' active' : ''}`} onClick={() => handleTabChange('all')}>
                        <div className="oa-kpi-label">Alert aperti</div>
                        <div className="oa-kpi-value">{summary.totalOpen}</div>
                    </div>
                    <div className={`oa-kpi-card kpi-critical${filters.severity === 'CRITICAL' ? ' active' : ''}`}
                        onClick={() => handleFilterChange('severity', filters.severity === 'CRITICAL' ? '' : 'CRITICAL')}>
                        <div className="oa-kpi-label">Critici</div>
                        <div className="oa-kpi-value">{summary.criticalCount}</div>
                    </div>
                    <div className={`oa-kpi-card kpi-high${filters.severity === 'HIGH' ? ' active' : ''}`}
                        onClick={() => handleFilterChange('severity', filters.severity === 'HIGH' ? '' : 'HIGH')}>
                        <div className="oa-kpi-label">Alta priorità</div>
                        <div className="oa-kpi-value">{summary.highCount}</div>
                    </div>
                    <div className={`oa-kpi-card${activeTab === 'device' ? ' active' : ''}`} onClick={() => handleTabChange('device')}>
                        <div className="oa-kpi-label">Device</div>
                        <div className="oa-kpi-value">{summary.byCategory?.DEVICE || 0}</div>
                    </div>
                    <div className={`oa-kpi-card${activeTab === 'worker' ? ' active' : ''}`} onClick={() => handleTabChange('worker')}>
                        <div className="oa-kpi-label">Worker</div>
                        <div className="oa-kpi-value">{(summary.byCategory?.WORKER || 0) + (summary.byCategory?.PAYOUT || 0)}</div>
                    </div>
                    <div className={`oa-kpi-card${activeTab === 'invoicing' ? ' active' : ''}`} onClick={() => handleTabChange('invoicing')}>
                        <div className="oa-kpi-label">Fatturazione</div>
                        <div className="oa-kpi-value">{summary.byCategory?.INVOICING || 0}</div>
                    </div>
                </div>
            )}

            {/* Error banner */}
            {error && <div className="oa-error-banner">⚠️ {error}</div>}

            {/* Filters */}
            <div className="oa-filters-box">
                <div className="oa-filters-row">
                    <div className="oa-filter-group oa-filter-group--sm">
                        <label>Severità</label>
                        <select className="oa-filter-select" value={filters.severity} onChange={e => handleFilterChange('severity', e.target.value)}>
                            <option value="">Tutte</option>
                            {SEVERITY_ORDER.map(s => <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>)}
                        </select>
                    </div>
                    <div className="oa-filter-group oa-filter-group--md">
                        <label>Stato</label>
                        <select className="oa-filter-select" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                            <option value="">Tutti</option>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div className="oa-filter-group oa-filter-group--sm">
                        <label>Da</label>
                        <input type="date" className="oa-filter-input" value={filters.from} onChange={e => handleFilterChange('from', e.target.value)} />
                    </div>
                    <div className="oa-filter-group oa-filter-group--sm">
                        <label>A</label>
                        <input type="date" className="oa-filter-input" value={filters.to} onChange={e => handleFilterChange('to', e.target.value)} />
                    </div>
                    <div className="oa-filter-group oa-filter-group--search">
                        <label>Cerca</label>
                        <input
                            type="text"
                            className="oa-filter-input"
                            placeholder="Titolo, descrizione..."
                            value={filters.search}
                            onChange={e => handleFilterChange('search', e.target.value)}
                        />
                    </div>
                    <div className="oa-filter-actions">
                        <button className="oa-btn oa-btn--secondary" onClick={() => setFilters({ severity: '', status: '', tenantId: '', search: '', from: '', to: '', page: 1 })}>
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="oa-tabs">
                {TABS.map(tab => {
                    const count = tabCount(tab);
                    return (
                        <button
                            key={tab.key}
                            className={`oa-tab${activeTab === tab.key ? ' active' : ''}`}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                            {count !== null && count > 0 && <span className="oa-tab-badge">{count}</span>}
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div className="oa-table-wrapper">
                <table className="oa-table">
                    <thead>
                        <tr>
                            <th>Severità</th>
                            <th>Categoria</th>
                            <th>Alert</th>
                            <th>Tenant</th>
                            <th>Venue</th>
                            <th>Prima occ.</th>
                            <th>Ultima occ.</th>
                            <th style={{ textAlign: 'center' }}>N</th>
                            <th>Stato</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr className="oa-loading-row">
                                <td colSpan="10">Caricamento...</td>
                            </tr>
                        )}
                        {!loading && alerts.length === 0 && (
                            <tr>
                                <td colSpan="10" className="oa-table-empty">
                                    {activeTab === 'resolved' ? '🎉 Nessun alert risolto in questo periodo.' : '✅ Nessun alert aperto. Tutto ok!'}
                                </td>
                            </tr>
                        )}
                        {!loading && alerts.map(alert => (
                            <tr key={alert.id} onClick={() => setSelectedAlert(alert)}>
                                <td><SeverityBadge severity={alert.severity} /></td>
                                <td><CategoryBadge category={alert.category} /></td>
                                <td style={{ maxWidth: 260 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {alert.title}
                                    </div>
                                </td>
                                <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                    {alert.tenant?.adminAlias || alert.tenant?.company_name || (alert.tenantId ? alert.tenantId.slice(0, 8) + '…' : '–')}
                                </td>
                                <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                    {alert.venue?.name || (alert.venueId ? alert.venueId.slice(0, 8) + '…' : '–')}
                                </td>
                                <td style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(alert.firstSeenAt)}</td>
                                <td style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(alert.lastSeenAt)}</td>
                                <td style={{ textAlign: 'center', fontWeight: 600, fontSize: '0.82rem' }}>{alert.occurrenceCount}</td>
                                <td><StatusBadge status={alert.status} /></td>
                                <td>
                                    <div className="oa-row-actions" onClick={e => e.stopPropagation()}>
                                        {!['RESOLVED', 'AUTO_RESOLVED'].includes(alert.status) && alert.status !== 'ACKNOWLEDGED' && (
                                            <button
                                                className="oa-btn oa-btn--secondary"
                                                title="Prendi in carico"
                                                onClick={(e) => handleRowAction(() => operationalAlertsService.acknowledge(alert.id), e)}
                                            >
                                                ✅
                                            </button>
                                        )}
                                        {!['RESOLVED', 'AUTO_RESOLVED'].includes(alert.status) && (
                                            <button
                                                className="oa-btn oa-btn--secondary"
                                                title="Risolvi"
                                                onClick={(e) => handleRowAction(() => operationalAlertsService.resolve(alert.id, ''), e)}
                                            >
                                                ✓
                                            </button>
                                        )}
                                        <button
                                            className="oa-btn oa-btn--ghost"
                                            title="Dettaglio"
                                            onClick={(e) => { e.stopPropagation(); setSelectedAlert(alert); }}
                                        >
                                            →
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="oa-pagination">
                        <span>{pagination.total} alert totali</span>
                        <div className="oa-pagination-btns">
                            <button
                                className="oa-btn oa-btn--secondary"
                                disabled={filters.page <= 1}
                                onClick={() => handleFilterChange('page', filters.page - 1)}
                            >
                                ← Prec
                            </button>
                            <span style={{ padding: '0 8px', lineHeight: '28px' }}>
                                {filters.page} / {pagination.totalPages}
                            </span>
                            <button
                                className="oa-btn oa-btn--secondary"
                                disabled={filters.page >= pagination.totalPages}
                                onClick={() => handleFilterChange('page', filters.page + 1)}
                            >
                                Succ →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail drawer */}
            {selectedAlert && (
                <AlertDrawer
                    alert={selectedAlert}
                    onClose={() => setSelectedAlert(null)}
                    onAction={handleDrawerAction}
                />
            )}
        </div>
    );
}
